import { Button, Collapse, List, Table } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useMemo } from 'react';
import Layout from "../../Classes/Layout";
import { layoutViewerSliceDispatch, pickAFileSliceDispatch } from "../../Constants/ReduxConstants";
import { ReduxHelpers } from "../../Helpers/ReduxHelpers";
import LayoutCategoryFieldModal from "../LayoutCategoryFieldModal/LayoutCategoryFieldModal";
import LayoutCategoryModal from "../LayoutCategoryModal/LayoutCategoryModal";

// Memoized selectors to prevent unnecessary re-renders
const selectLayout = (state: any) => state.pickAFile?.layout;
const selectActiveLayoutCategoryIndex = (state: any) => state.layoutViewer?.activeLayoutCategoryIndex ?? 0;
const selectActiveLayoutTabIndex = (state: any) => state.layoutViewer?.activeLayoutTabIndex ?? 0;

// Create memoized selector that returns the same reference if layout hasn't changed
const selectMemoizedLayout = createSelector(
    [selectLayout],
    (layout) => layout
);

const selectMemoizedCategoryIndex = createSelector(
    [selectActiveLayoutCategoryIndex],
    (index) => index
);

const selectMemoizedTabIndex = createSelector(
    [selectActiveLayoutTabIndex],
    (index) => index
);

const LayoutViewer = () => {
    const currentLayout = useSelector(selectMemoizedLayout);

    const activeLayoutCategoryIndex = useSelector(selectMemoizedCategoryIndex);
    const activeLayoutTabIndex = useSelector(selectMemoizedTabIndex);

    const dispatch = useDispatch()

    const { CreateContentsFromStates } = ReduxHelpers();

    function EditLayout(layoutDetails) {
        let deleteInfo = layoutDetails.split("|")
        let tabName = deleteInfo.length > 0 ? deleteInfo[0] : null;
        let direction = deleteInfo.length > 0 ? deleteInfo[1] : null;
        let catIndex = deleteInfo.length > 0 ? deleteInfo[2] : null;
        let catName = deleteInfo.length > 0 ? deleteInfo[3] : null;
        let isField = deleteInfo.length > 0 ? deleteInfo[4] : null;
        let fieldIndex = deleteInfo.length > 0 ? deleteInfo[5] : null;

        //Parse out the details
        let skip = false;

        if (tabName && direction && catIndex && catName && isField && fieldIndex) {
            //nab the right layout
            let matchIndex = currentLayout.findIndex(l => l.tab == tabName && l.name == catName)
            let newIndex = -1

            if (direction === "up") {
                newIndex = parseInt(fieldIndex) - 1
            }
            if (direction === "down") {
                newIndex = parseInt(fieldIndex) + 1
            }
            let f = currentLayout[matchIndex].fields.splice(parseInt(fieldIndex), 1)[0];
            // insert stored item into position `to`
            currentLayout[matchIndex].fields.splice(newIndex, 0, f);
            skip = true;
        }

        if (tabName && direction && catIndex && catName && !skip) {
            //nab the right layout
            let matchIndex = currentLayout.findIndex(l => l.tab == tabName && l.name == catName)
            let newIndex = -1

            if (direction === "up") {
                let nextPositionIndex = currentLayout.findLastIndex(l => l.tab == tabName && l.name != catName)
                if ((nextPositionIndex > -1 && nextPositionIndex < matchIndex - 1)) {
                    newIndex = nextPositionIndex
                }
                else {
                    newIndex = matchIndex - 1
                }
            }
            if (direction === "down") {
                let nextPositionIndex = currentLayout.findIndex(l => l.tab == tabName && l.name != catName)
                if ((nextPositionIndex > -1 && nextPositionIndex > matchIndex + 1)) {
                    newIndex = nextPositionIndex
                }
                else {
                    newIndex = matchIndex + 1
                }
            }
            let f = currentLayout.splice(matchIndex, 1)[0];
            // insert stored item into position `to`
            currentLayout.splice(newIndex, 0, f);
        }

        dispatch({ type: pickAFileSliceDispatch.setLayout, payload: currentLayout })

        CreateContentsFromStates({ layout: currentLayout });
    }

    function DeleteLayout(layoutToDelete) {
        let deleteInfo = layoutToDelete.split("|")
        let tabName = deleteInfo.length > 0 ? deleteInfo[0] : null;
        let catName = deleteInfo.length > 0 ? deleteInfo[1] : null;
        let fieldIndex = deleteInfo.length > 0 ? deleteInfo[2] : null;

        if (tabName && catName && fieldIndex) {
            const indexToEdit = currentLayout.findIndex(e => e.tab == tabName && e.name == catName)

            //Find the right section
            let filteredLayout = currentLayout.filter(layout => layout.tab == tabName && layout.name == catName)
            filteredLayout[0]?.fields.splice(fieldIndex, 1)

            currentLayout[indexToEdit] = filteredLayout[0];

            CreateContentsFromStates({ layout: currentLayout });
            return currentLayout
        }

        if (tabName && catName) {
            const indexToEdit = currentLayout.findIndex(e => e.tab == tabName && e.name == catName)

            //Find the right section
            currentLayout.splice(indexToEdit, 1)

            dispatch({ type: pickAFileSliceDispatch.setLayout, payload: currentLayout })

            CreateContentsFromStates({ layout: currentLayout });
            return currentLayout
        }
    }

    const allTabs = [
        { key: 'patient', text: 'Patient', icon: 'user', index: 0 },
        { key: 'insurance', text: 'Insurance', icon: 'dollar sign', index: 1 },
        { key: 'income & eligibility', text: 'Income & Eligibility', icon: 'money bill alternate', index: 2 },
        { key: 'medical', text: 'Medical', icon: 'heartbeat', index: 3 },
        { key: 'provider', text: 'Provider', icon: 'stethoscope', index: 4 },
        { key: 'order / logistics', text: 'Order & Logistics', icon: 'clipboard list', index: 5 },
        { key: 'other', text: 'Other', icon: 'info', index: 6 },
        { key: 'attestation & signature', text: 'Attestation & Signature', icon: 'clipboard check', index: 7 }
    ]

    const HandleDelete = (event) => {
        DeleteLayout(event.currentTarget.id)
    }

    const handleCategoryClick = (value) => {
        dispatch({ type: layoutViewerSliceDispatch.setActiveLayoutCategoryIndex, payload: parseInt(value.id) })
    }

    const handleTabClick = (value) => {
        dispatch({ type: layoutViewerSliceDispatch.setActiveLayoutTabIndex, payload: parseInt(value.id) })
    }

    const handleMoveUp = (e) => {
        const a = 0;
        EditLayout(e.currentTarget.id)
    }

    const handleMoveDown = (e) => {
        const a = 0;
        EditLayout(e.currentTarget.id)
    }

    let rowCounter = 0;

    function compareTabKeys(r, s) {

        if (s.key == r) {
            return (true)
        } else {
            return (false)
        }
    }

    function getGroupedTabs() {
        if (currentLayout) {
            let groupedByTabs = currentLayout?.reduce((layout, {
                tab,
                fields,
                name
            }) => {
                (layout[tab] = layout[tab] || []).push({ tab, fields, name });
                return layout;
            }, {})

            return groupedByTabs

        }
        return null
    }

    function sortLayoutGroups(a, b) {
        let tabA = a[0];
        let tabB = b[0];

        let foundA = allTabs.filter(tab => tab.key === tabA)
        let foundB = allTabs.filter(tab => tab.key === tabB)

        let indexA = foundA[0].index
        let indexB = foundB[0].index

        if (indexA < indexB) {
            return -1;
        }
        if (indexA > indexB) {
            return 1;
        }
    }

    let allgroups = getGroupedTabs();

    let listOfTabsThatAreUsed = allgroups ? Object.entries(allgroups) : []

    let onesToAdd = allTabs?.filter(s => !listOfTabsThatAreUsed?.some(r => compareTabKeys(r[0], s)))

    let thing: any[] = allgroups ? Object.entries(allgroups) : []


    if (onesToAdd?.length > 0) {
        for (let itemToAdd in onesToAdd) {
            let jsonToAdd: any[] = []
            jsonToAdd.push(onesToAdd[itemToAdd].key)
            jsonToAdd.push([])
            thing.splice(onesToAdd[itemToAdd].index, 0, jsonToAdd as any)
        }
    }
    //Sort the groups by their index
    thing.sort((a, b) => sortLayoutGroups(a, b))

    const allLayout =
        thing.map((currentTab, currentTabIndex) => {

            let tabContent =
                <div>{currentTab[0]}  </div>

            let categoryContent =
                currentTab[1].map((currentCategory, categoryIndex) => {

                    let catHeaderContent =
                        <div>
                            <div>{currentCategory.name}</div>
                        </div>

                    let columns = [
                        {
                            title: 'Item Name',
                            dataIndex: 'ItemName',
                            key: 'ItemName',
                            render: (text) => <p>{text}</p>
                        },
                        {
                            title: 'Move Up',
                            dataIndex: 'MoveUp',
                            key: 'MoveUp',
                            render: (text) => <Button
                                disabled={text.buttonDisable == 0}
                                id={text.buttonId}
                                onClick={handleMoveUp}
                                key="UpButton_{{ rowCounter }}">
                                Move Up
                            </Button>
                        },
                        {
                            title: 'Move Down',
                            dataIndex: 'MoveDown',
                            key: 'MoveDown',
                            render: (text) => <Button
                                disabled={text.buttonDisable == currentCategory.fields.length - 1}
                                id={text.buttonId}
                                onClick={handleMoveDown}
                                key="DownButton_{{ rowCounter }}">
                                Move Down
                            </Button>
                        },
                        {
                            title: 'Delete',
                            dataIndex: 'Delete',
                            key: 'Delete',
                            render: (text) => <Button
                                onClick={HandleDelete}
                                id={currentTab[0] + "|" + currentCategory.name + "|" + text}
                            >Delete
                            </Button>
                        }
                    ]

                    let data = currentCategory.fields.map((currentField, fieldIndex) => {
                        return {
                            ItemName: currentField,
                            key: fieldIndex,
                            MoveUp: {
                                buttonId: currentTab[0] + "|up|" + categoryIndex + "|" + currentCategory.name + "|field|" + fieldIndex,
                                buttonDisable: fieldIndex
                            },
                            MoveDown: {
                                buttonId: currentTab[0] + "|down|" + categoryIndex + "|" + currentCategory.name + "|field|" + fieldIndex,
                                buttonDisable: fieldIndex
                            },
                            Delete: fieldIndex
                        }
                    })

                    let tabContent = <Table columns={columns} dataSource={data} pagination={false} />

                    return (
                        <List>
                            <Collapse
                                defaultActiveKey={[activeLayoutCategoryIndex]}
                                style={{ width: '100%' }}
                                onChange={() => handleCategoryClick({ target: { id: categoryIndex } })}
                            >
                                <Collapse.Panel key={categoryIndex} header={catHeaderContent} style={{ width: '100%' }}>
                                    {tabContent}
                                </Collapse.Panel>
                            </Collapse>
                            <LayoutCategoryFieldModal caption={"Add Question to Category"} layout={currentCategory} />
                            <Button
                                id={currentTab[0] + "|up|" + categoryIndex + "|" + currentCategory?.name}
                                onClick={handleMoveUp}
                                disabled={categoryIndex == 0}
                            >Move Up</Button>
                            <Button
                                id={currentTab[0] + "|down|" + categoryIndex + "|" + currentCategory?.name}
                                onClick={handleMoveDown}
                                disabled={categoryIndex == currentTab[1].length - 1}
                            >Move Down</Button>
                            <Button
                                id={currentTab[0] + "|" + currentCategory?.name}
                                onClick={HandleDelete}
                            >Delete</Button>
                        </List>
                    )
                }
                )

            let defaultNewLayout = new Layout()
            defaultNewLayout.tab = currentTab[0]
            categoryContent = <><LayoutCategoryModal caption={"Add Category to Tab"} layout={defaultNewLayout} />{categoryContent}</>

            return (
                <List>
                    <Collapse
                        defaultActiveKey={[activeLayoutTabIndex]}
                        style={{ width: '100%' }}
                        onChange={() => handleTabClick({ target: { id: currentTabIndex } })}
                    >
                        <Collapse.Panel key={currentTabIndex} header={tabContent} style={{ width: '100%' }}>
                            {categoryContent}
                        </Collapse.Panel>
                    </Collapse>
                </List>
            )
        }
        )

    return <div>{allLayout}</div>
}

export default LayoutViewer;
